import type { ConfirmDialogProps } from './ConfirmDialog';

/** What `confirm` asks: the dialog's text and, optionally, the action it runs. */
export type ConfirmRequest = Omit<ConfirmDialogProps, 'isOpen' | 'onClose' | 'onConfirm'> & {
    onConfirm?: () => unknown;
};

export interface ConfirmEntry {
    id: number;
    request: ConfirmRequest;
    /** False once answered, while the dialog fades out. */
    open: boolean;
    confirmed: boolean;
    resolve: (confirmed: boolean) => void;
}

let entries: ConfirmEntry[] = [];
let nextId = 1;
const listeners = new Set<() => void>();

function emit(): void {
    for (const listener of listeners) listener();
}

export function subscribeConfirms(listener: () => void): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
}

export function getConfirms(): ConfirmEntry[] {
    return entries;
}

/**
 * Asks in the portal's ConfirmDialog and resolves `true` once confirmed (and, with
 * `onConfirm`, once that action has succeeded) or `false` when cancelled. Needs a
 * `ConfirmHost` mounted, as toasts need the Toaster.
 */
export function confirm(request: ConfirmRequest): Promise<boolean> {
    return new Promise((resolve) => {
        entries = [...entries, { id: nextId++, request, open: true, confirmed: false, resolve }];
        emit();
    });
}

/** Records the answer and starts the close; the host removes the entry once it has faded. */
export function answerConfirm(id: number, confirmed: boolean): void {
    const entry = entries.find((candidate) => candidate.id === id);
    if (!entry?.open) return;
    entries = entries.map((candidate) =>
        candidate.id === id ? { ...candidate, open: false, confirmed } : candidate
    );
    entry.resolve(confirmed);
    emit();
}

export function removeConfirm(id: number): void {
    entries = entries.filter((candidate) => candidate.id !== id);
    emit();
}
